import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from './ui/alert-dialog';
import { Button } from './animate-ui/components/buttons/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { apiKeysApi, ApiKeyCreatedResponse, ApiKeySummary } from '../services/apiKeysApi';
import { useToast } from './ui/toast';
import { useTranslation } from '../i18n';

interface ApiKeysManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatTimestamp = (ts?: number | null) => {
  if (!ts) return 'N/A';
  return new Date(ts * 1000).toLocaleString();
};

export const ApiKeysManager: React.FC<ApiKeysManagerProps> = ({ open, onOpenChange }) => {
  const { push } = useToast();
  const { t } = useTranslation();
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState<ApiKeyCreatedResponse | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const sortedKeys = useMemo(() => {
    const filtered = showRevoked 
      ? keys 
      : keys.filter(k => !k.revoked_at);
    return filtered.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
  }, [keys, showRevoked]);

  const revokedCount = useMemo(() => {
    return keys.filter(k => k.revoked_at).length;
  }, [keys]);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiKeysApi.list();
      setKeys(data || []);
    } catch (err: any) {
      setError(err?.message || t('api_keys.error_load', 'Failed to load API keys.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      loadKeys();
      setCreatedKey(null);
      setError(null);
    }
  }, [open, loadKeys]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const created = await apiKeysApi.create(name.trim() || undefined);
      setCreatedKey(created);
      setName('');
      await loadKeys();
      push({ 
        title: t('api_keys.toast_created_title', 'API key created'), 
        description: t('api_keys.toast_created_description', 'Copy it now. It will not be shown again.') 
      });
    } catch (err: any) {
      setError(err?.message || t('api_keys.error_create', 'Failed to create API key.'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setRevokingId(id);
    setError(null);
    try {
      await apiKeysApi.revoke(id);
      await loadKeys();
      push({ title: t('api_keys.toast_revoked', 'API key revoked') });
    } catch (err: any) {
      setError(err?.message || t('api_keys.error_revoke', 'Failed to revoke API key.'));
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopy = async () => {
    if (!createdKey?.api_key) return;
    try {
      await navigator.clipboard.writeText(createdKey.api_key);
      push({ title: t('api_keys.toast_copied', 'Copied to clipboard') });
    } catch (err: any) {
      setError(err?.message || t('api_keys.error_copy', 'Failed to copy API key.'));
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('api_keys.title', 'API Keys')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('api_keys.description', 'Generate keys for programmatic access. Keys are shown only once.')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="api-key-name">{t('api_keys.name_label', 'Name (optional)')}</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="api-key-name"
                value={name}
                placeholder={t('api_keys.name_placeholder', 'e.g. Analytics pipeline')}
                onChange={(e) => setName(e.target.value)}
              />
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? t('api_keys.generating', 'Creating...') : t('api_keys.generate_button', 'Generate')}
              </Button>
            </div>
          </div>

          {createdKey && (
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
              <div className="text-sm font-medium">{t('api_keys.new_key_title', 'New API key')}</div>
              <div className="rounded bg-background px-3 py-2 font-mono text-xs break-all border">
                {createdKey.api_key}
              </div>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {t('api_keys.copy_button', 'Copy')}
              </Button>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{t('api_keys.existing_keys_title', 'Existing keys')}</div>
              <div className="flex items-center gap-2">
                {revokedCount > 0 && (
                  <button
                    onClick={() => setShowRevoked(!showRevoked)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showRevoked 
                      ? t('api_keys.hide_revoked', 'Hide revoked ({{count}})', { count: revokedCount })
                      : t('api_keys.show_revoked', 'Show revoked ({{count}})', { count: revokedCount })
                    }
                  </button>
                )}
                {loading && <span className="text-xs text-muted-foreground">{t('api_keys.loading', 'Loading...')}</span>}
              </div>
            </div>
            {sortedKeys.length === 0 && !loading ? (
              <div className="text-sm text-muted-foreground">
                {showRevoked 
                  ? t('api_keys.no_revoked_keys', 'No revoked keys.')
                  : t('api_keys.no_active_keys', 'No active API keys yet.')
                }
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto pr-1">
                {sortedKeys.map((key) => {
                  const isRevoked = Boolean(key.revoked_at);
                  return (
                    <div
                      key={key.id}
                      className="flex flex-col gap-2 rounded-lg border bg-background p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{key.name || t('api_keys.untitled_key', 'Untitled key')}</span>
                          {isRevoked ? (
                            <Badge variant="destructive">{t('api_keys.badge_revoked', 'Revoked')}</Badge>
                          ) : (
                            <Badge variant="outline">{t('api_keys.badge_active', 'Active')}</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('api_keys.prefix_label', 'Prefix:')} {key.key_prefix} | {t('api_keys.created_label', 'Created:')} {formatTimestamp(key.created_at)} | {t('api_keys.last_used_label', 'Last used:')} {formatTimestamp(key.last_used_at)}
                        </div>
                      </div>
                      {!isRevoked && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={revokingId === key.id}
                          onClick={() => handleRevoke(key.id)}
                        >
                          {revokingId === key.id ? t('api_keys.revoking', 'Revoking...') : t('api_keys.revoke_button', 'Revoke')}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('api_keys.close_button', 'Close')}</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ApiKeysManager;
